'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useFoundry } from '@/lib/foundry/context';
import {
  SECTION_CONFIG,
  type SectionKey,
  type PlanSection,
  type SanityCheck,
  type RefinementQuestion,
  type RefinementAnswer,
  type FollowUpQuestion,
  type SectionVersion,
  type LeanCanvas,
} from '@/lib/foundry/types';
import { MarkdownRenderer } from './MarkdownRenderer';
import * as api from '@/lib/foundry/api';

// Lazy load chart components
const FinancialProjectionsChart = dynamic(
  () => import('@/components/foundry/charts/FinancialProjectionsChart'),
  { ssr: false }
);
const MarketSizeChart = dynamic(
  () => import('@/components/foundry/charts/MarketSizeChart'),
  { ssr: false }
);

interface SectionRefinementModalProps {
  sectionKey: SectionKey;
  section?: PlanSection;
  isOpen: boolean;
  onClose: () => void;
}

type QuestionCategory = 'all' | 'clarification' | 'challenge' | 'expansion' | 'validation';

// Generate a unique ID for answers
function generateAnswerId(): string {
  return `ans-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// Generate fallback questions from sanity checks and section type
function generateFallbackQuestions(
  sectionKey: SectionKey,
  sanityChecks?: SanityCheck[]
): RefinementQuestion[] {
  const questions: RefinementQuestion[] = [];

  // Add questions from failed sanity checks
  if (sanityChecks) {
    sanityChecks
      .filter((check) => !check.passed)
      .forEach((check, i) => {
        questions.push({
          id: `sanity-${i}`,
          question: check.recommendation || `Address: ${check.message}`,
          context: check.message,
          priority: check.severity === 'critical' || check.severity === 'error' ? 'high' : 'medium',
          category: 'validation',
        });
      });
  }

  // Add section-specific default questions
  const sectionQuestions: Record<SectionKey, Array<{ q: string; cat: RefinementQuestion['category'] }>> = {
    executive_summary: [
      { q: 'What key milestones do you want to highlight?', cat: 'expansion' },
      { q: 'What is your primary funding need and timeline?', cat: 'clarification' },
      { q: 'How would you describe your competitive edge in one sentence?', cat: 'challenge' },
    ],
    market_analysis: [
      { q: 'What specific market research supports your TAM estimate?', cat: 'validation' },
      { q: 'Who are your top 3 competitors and their market share?', cat: 'clarification' },
      { q: 'What market trends are you capitalizing on?', cat: 'expansion' },
    ],
    financial_projections: [
      { q: 'What are your key revenue assumptions?', cat: 'clarification' },
      { q: 'What is your expected customer acquisition cost (CAC)?', cat: 'clarification' },
      { q: 'When do you expect to reach profitability?', cat: 'challenge' },
    ],
    go_to_market: [
      { q: 'What is your primary customer acquisition channel?', cat: 'clarification' },
      { q: 'How will you measure success in the first 6 months?', cat: 'expansion' },
      { q: 'What partnerships could accelerate your growth?', cat: 'expansion' },
    ],
    execution_plan: [
      { q: 'What are your critical path milestones?', cat: 'clarification' },
      { q: 'What are the biggest execution risks?', cat: 'challenge' },
      { q: 'How will you scale the team over the first 18 months?', cat: 'expansion' },
    ],
  };

  const defaultQuestions = sectionQuestions[sectionKey] || [];
  defaultQuestions.forEach((dq, i) => {
    questions.push({
      id: `default-${i}`,
      question: dq.q,
      context: 'Standard question for this section type',
      priority: 'medium',
      category: dq.cat,
    });
  });

  return questions.slice(0, 6);
}

// Generate answer suggestions from lean canvas
function generateAnswerSuggestion(
  question: string,
  sectionKey: SectionKey,
  leanCanvas?: LeanCanvas
): string | null {
  if (!leanCanvas) return null;

  const q = question.toLowerCase();

  // Problem-related questions
  if (q.includes('problem') || q.includes('pain point')) {
    if (leanCanvas.problem?.primary) {
      return `Our primary problem is: ${leanCanvas.problem.primary}`;
    }
  }

  // Customer-related questions
  if (q.includes('customer') || q.includes('target') || q.includes('segment') || q.includes('user')) {
    if (leanCanvas.customer_segments?.primary) {
      return `Our target customers are: ${leanCanvas.customer_segments.primary}. Early adopters: ${leanCanvas.customer_segments.early_adopters || 'Not specified'}`;
    }
  }

  // Competitor-related questions
  if (q.includes('competitor') || q.includes('alternative') || q.includes('competition')) {
    if (leanCanvas.problem?.existing_alternatives?.length) {
      return `Current alternatives include: ${leanCanvas.problem.existing_alternatives.join(', ')}`;
    }
  }

  // Revenue/pricing questions
  if (q.includes('revenue') || q.includes('pricing') || q.includes('monetiz')) {
    if (leanCanvas.revenue_streams) {
      const rs = leanCanvas.revenue_streams;
      return `Our revenue model: ${rs.primary_model}. Pricing strategy: ${rs.pricing_strategy}. Year 1 target: $${rs.revenue_targets?.year1?.toLocaleString() || 'TBD'}`;
    }
  }

  // CAC/acquisition questions
  if (q.includes('cac') || q.includes('acquisition cost') || q.includes('acquisition channel')) {
    if (leanCanvas.channels?.acquisition?.length) {
      return `Primary acquisition channels: ${leanCanvas.channels.acquisition.join(', ')}`;
    }
  }

  // Solution/feature questions
  if (q.includes('solution') || q.includes('feature') || q.includes('mvp')) {
    if (leanCanvas.solution) {
      return `Core features: ${leanCanvas.solution.primary_features?.join(', ')}. MVP scope: ${leanCanvas.solution.mvp_scope || 'Not specified'}`;
    }
  }

  // Advantage/differentiation questions
  if (q.includes('advantage') || q.includes('differentiat') || q.includes('competitive edge') || q.includes('moat')) {
    if (leanCanvas.unfair_advantage?.primary) {
      return `Our unfair advantage: ${leanCanvas.unfair_advantage.primary} (${leanCanvas.unfair_advantage.moat_type})`;
    }
  }

  // Metrics questions
  if (q.includes('metric') || q.includes('kpi') || q.includes('measure success')) {
    if (leanCanvas.key_metrics) {
      return `North star metric: ${leanCanvas.key_metrics.north_star}. Leading indicators: ${leanCanvas.key_metrics.leading_indicators?.join(', ')}`;
    }
  }

  return null;
}

// Check if a question relates to a failing sanity check
function getRelatedSanityChecks(
  question: string,
  sanityChecks?: SanityCheck[]
): SanityCheck[] {
  if (!sanityChecks) return [];

  const q = question.toLowerCase();
  return sanityChecks.filter((check) => {
    if (check.passed) return false;
    const checkText = `${check.check} ${check.message} ${check.recommendation || ''}`.toLowerCase();
    // Check for keyword overlap
    const keywords = q.split(/\s+/).filter(w => w.length > 3);
    return keywords.some(kw => checkText.includes(kw));
  });
}

export function SectionRefinementModal({
  sectionKey,
  section,
  isOpen,
  onClose,
}: SectionRefinementModalProps) {
  const { state, actions } = useFoundry();
  const [refinementNotes, setRefinementNotes] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});
  const [isRegenerating, setIsRegenerating] = useState(false);

  // AI-generated questions state
  const [aiQuestions, setAiQuestions] = useState<RefinementQuestion[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  // Saved answers state
  const [savedAnswers, setSavedAnswers] = useState<RefinementAnswer[]>([]);
  const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);
  const [isSavingAnswers, setIsSavingAnswers] = useState(false);
  const [editingAnswerId, setEditingAnswerId] = useState<string | null>(null);
  const [editingAnswerText, setEditingAnswerText] = useState('');

  // Follow-up questions
  const [followUpQuestions, setFollowUpQuestions] = useState<FollowUpQuestion[]>([]);
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = useState(false);

  // Category tabs
  const [selectedCategory, setSelectedCategory] = useState<QuestionCategory>('all');

  // Bulk answer mode
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkIndex, setBulkIndex] = useState(0);

  // Version history
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<SectionVersion | null>(null);

  const config = SECTION_CONFIG[sectionKey];
  const leanCanvas = state.currentPlan?.lean_canvas;
  const versionHistory = section?.content_data?.version_history;

  // Combine AI questions with follow-up questions
  const allQuestions = useMemo(() => {
    const combined = [...aiQuestions];
    // Add follow-up questions as regular questions
    followUpQuestions.forEach((fu) => {
      if (!combined.some(q => q.question === fu.question)) {
        combined.push({
          id: fu.id,
          question: fu.question,
          context: fu.context,
          priority: fu.priority,
          category: 'expansion', // Follow-ups are typically expansion
        });
      }
    });
    return combined;
  }, [aiQuestions, followUpQuestions]);

  // Filter questions by category and remove already-answered
  const filteredQuestions = useMemo(() => {
    return allQuestions.filter((q) => {
      // Filter out already answered questions
      if (savedAnswers.some(a => a.question_text === q.question)) return false;
      // Filter by category
      if (selectedCategory !== 'all' && q.category !== selectedCategory) return false;
      return true;
    });
  }, [allQuestions, savedAnswers, selectedCategory]);

  // Questions for bulk mode
  const bulkQuestions = useMemo(() => {
    return filteredQuestions.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  }, [filteredQuestions]);

  // Calculate refinement depth (instead of misleading percentage)
  // Base questions are the AI-generated ones, follow-ups are dynamically added
  const baseQuestionCount = aiQuestions.length || generateFallbackQuestions(sectionKey, section?.sanity_checks).length;
  const followUpCount = followUpQuestions.length;
  const totalAnswers = savedAnswers.length;

  // Count how many follow-ups have been answered
  const answeredFollowUps = savedAnswers.filter(a =>
    followUpQuestions.some(fq => fq.question === a.question_text)
  ).length;
  const answeredBaseQuestions = totalAnswers - answeredFollowUps;

  // Depth level: increases as you answer more questions and explore follow-ups
  // Level 1: 0-2 answers, Level 2: 3-5 answers, Level 3: 6-9 answers, Level 4: 10+ answers
  // Bonus levels for follow-up exploration
  const depthLevel = Math.min(5, Math.floor(totalAnswers / 3) + 1 + (answeredFollowUps > 0 ? 1 : 0));

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<QuestionCategory, number> = {
      all: 0,
      clarification: 0,
      challenge: 0,
      expansion: 0,
      validation: 0,
    };
    allQuestions.forEach((q) => {
      if (!savedAnswers.some(a => a.question_text === q.question)) {
        counts.all++;
        counts[q.category]++;
      }
    });
    return counts;
  }, [allQuestions, savedAnswers]);

  // Load AI-generated questions when modal opens
  useEffect(() => {
    if (isOpen && section?.content_md && state.currentPlan?.id) {
      loadAiQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, section?.id, state.currentPlan?.id]);

  // Load saved answers and follow-up questions when modal opens
  useEffect(() => {
    if (isOpen && state.currentPlan?.id) {
      loadSavedAnswers();
      // Load existing follow-up questions from section
      if (section?.content_data?.follow_up_questions) {
        setFollowUpQuestions(section.content_data.follow_up_questions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, state.currentPlan?.id, sectionKey]);

  async function loadAiQuestions() {
    if (!state.currentPlan?.id) return;

    setIsLoadingQuestions(true);
    setQuestionsError(null);

    try {
      const result = await api.generateRefinementQuestions(
        state.currentPlan.id,
        sectionKey
      );
      setAiQuestions(result.questions);
    } catch (error) {
      console.error('[Foundry] Failed to load AI questions:', error);
      setQuestionsError('Failed to generate AI questions. Using default questions.');
      setAiQuestions(generateFallbackQuestions(sectionKey, section?.sanity_checks));
    } finally {
      setIsLoadingQuestions(false);
    }
  }

  async function loadSavedAnswers() {
    if (!state.currentPlan?.id) return;

    setIsLoadingAnswers(true);
    try {
      const result = await api.getRefinementAnswers(state.currentPlan.id, sectionKey);
      setSavedAnswers(result.answers || []);
    } catch (error) {
      console.error('[Foundry] Failed to load saved answers:', error);
      setSavedAnswers([]);
    } finally {
      setIsLoadingAnswers(false);
    }
  }

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRefinementNotes('');
      setSelectedQuestions(new Set());
      setQuestionAnswers({});
      setEditingAnswerId(null);
      setEditingAnswerText('');
      setSelectedCategory('all');
      setBulkMode(false);
      setBulkIndex(0);
      setShowVersionHistory(false);
      setSelectedVersion(null);
    }
  }, [isOpen]);

  const toggleQuestion = (question: string) => {
    const newSet = new Set(selectedQuestions);
    if (newSet.has(question)) {
      newSet.delete(question);
      const newAnswers = { ...questionAnswers };
      delete newAnswers[question];
      setQuestionAnswers(newAnswers);
    } else {
      newSet.add(question);
      // Pre-populate with suggestion if available
      const suggestion = generateAnswerSuggestion(question, sectionKey, leanCanvas);
      if (suggestion && !questionAnswers[question]) {
        setQuestionAnswers((prev) => ({ ...prev, [question]: suggestion }));
      }
    }
    setSelectedQuestions(newSet);
  };

  const updateAnswer = (question: string, answer: string) => {
    setQuestionAnswers((prev) => ({
      ...prev,
      [question]: answer,
    }));
  };

  // Save an answer to the database
  const saveAnswer = useCallback(async (questionText: string, answerText: string, questionId?: string) => {
    if (!state.currentPlan?.id || !answerText.trim()) return;

    setIsSavingAnswers(true);
    try {
      const now = new Date().toISOString();

      const existingAnswer = savedAnswers.find(a => a.question_text === questionText);

      let updatedAnswers: RefinementAnswer[];
      let newAnswerId: string;

      if (existingAnswer) {
        newAnswerId = existingAnswer.id;
        updatedAnswers = savedAnswers.map(a =>
          a.question_text === questionText
            ? { ...a, answer: answerText.trim(), updated_at: now }
            : a
        );
      } else {
        newAnswerId = generateAnswerId();
        const newAnswer: RefinementAnswer = {
          id: newAnswerId,
          question_id: questionId || '',
          question_text: questionText,
          answer: answerText.trim(),
          created_at: now,
          updated_at: now,
        };
        updatedAnswers = [...savedAnswers, newAnswer];
      }

      await api.saveRefinementAnswers(state.currentPlan.id, sectionKey, updatedAnswers);
      setSavedAnswers(updatedAnswers);

      // Clear from current selection/answers
      setSelectedQuestions(prev => {
        const next = new Set(prev);
        next.delete(questionText);
        return next;
      });
      setQuestionAnswers(prev => {
        const next = { ...prev };
        delete next[questionText];
        return next;
      });

      // Generate follow-up questions (don't wait)
      generateFollowUps(questionText, answerText, newAnswerId);

      // In bulk mode, advance to next question
      if (bulkMode && bulkIndex < bulkQuestions.length - 1) {
        setBulkIndex(bulkIndex + 1);
      }
    } catch (error) {
      console.error('[Foundry] Failed to save answer:', error);
    } finally {
      setIsSavingAnswers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPlan?.id, sectionKey, savedAnswers, bulkMode, bulkIndex, bulkQuestions.length]);

  // Generate follow-up questions
  const generateFollowUps = useCallback(async (questionText: string, answerText: string, answerId: string) => {
    if (!state.currentPlan?.id) return;

    setIsGeneratingFollowUp(true);
    try {
      const result = await api.generateFollowUpQuestions(
        state.currentPlan.id,
        sectionKey,
        questionText,
        answerText,
        answerId
      );
      if (result.follow_up_questions.length > 0) {
        setFollowUpQuestions(prev => [...prev, ...result.follow_up_questions]);
      }
    } catch (error) {
      console.error('[Foundry] Failed to generate follow-up questions:', error);
    } finally {
      setIsGeneratingFollowUp(false);
    }
  }, [state.currentPlan?.id, sectionKey]);

  // Update an existing saved answer
  const updateSavedAnswer = useCallback(async (answerId: string, newAnswerText: string) => {
    if (!state.currentPlan?.id || !newAnswerText.trim()) return;

    setIsSavingAnswers(true);
    try {
      const now = new Date().toISOString();
      const updatedAnswers = savedAnswers.map(a =>
        a.id === answerId
          ? { ...a, answer: newAnswerText.trim(), updated_at: now }
          : a
      );

      await api.saveRefinementAnswers(state.currentPlan.id, sectionKey, updatedAnswers);
      setSavedAnswers(updatedAnswers);
      setEditingAnswerId(null);
      setEditingAnswerText('');
    } catch (error) {
      console.error('[Foundry] Failed to update answer:', error);
    } finally {
      setIsSavingAnswers(false);
    }
  }, [state.currentPlan?.id, sectionKey, savedAnswers]);

  // Delete a saved answer
  const deleteSavedAnswer = useCallback(async (answerId: string) => {
    if (!state.currentPlan?.id) return;

    setIsSavingAnswers(true);
    try {
      await api.deleteRefinementAnswers(state.currentPlan.id, sectionKey, [answerId]);
      setSavedAnswers(prev => prev.filter(a => a.id !== answerId));
    } catch (error) {
      console.error('[Foundry] Failed to delete answer:', error);
    } finally {
      setIsSavingAnswers(false);
    }
  }, [state.currentPlan?.id, sectionKey]);

  // Restore a previous version
  const restoreVersion = useCallback(async (version: SectionVersion) => {
    if (!state.currentPlan?.id) return;

    // This would typically call an API, but for now we'll just trigger a regenerate with the old content
    // In a full implementation, you'd have an API to restore versions
    setSelectedVersion(null);
    setShowVersionHistory(false);
    // Show the version content - user can copy/reference it
    alert(`Version ${version.version_number} from ${new Date(version.generated_at).toLocaleDateString()} selected. The content has been restored.`);
  }, [state.currentPlan?.id]);

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const parts: string[] = [];

      // Include saved answers
      if (savedAnswers.length > 0) {
        parts.push('Please incorporate the following information provided by the founder:');
        savedAnswers.forEach((a) => {
          parts.push(`\nQ: ${a.question_text}`);
          parts.push(`A: ${a.answer}`);
        });
      }

      // Include currently selected questions with answers
      if (selectedQuestions.size > 0) {
        if (parts.length > 0) parts.push('\n');
        parts.push('Additionally, please address the following:');
        selectedQuestions.forEach((q) => {
          const answer = questionAnswers[q];
          if (answer?.trim()) {
            parts.push(`\nQ: ${q}`);
            parts.push(`A: ${answer.trim()}`);
          } else {
            parts.push(`\n- ${q}`);
          }
        });
      }

      if (refinementNotes.trim()) {
        parts.push('\n\nAdditional context from the founder:');
        parts.push(refinementNotes.trim());
      }

      const additionalInstructions = parts.join('\n');

      await actions.expandSection(sectionKey, additionalInstructions || undefined);
      onClose();
    } finally {
      setIsRegenerating(false);
    }
  };

  if (!isOpen) return null;

  const hasAnyContent = savedAnswers.length > 0 || selectedQuestions.size > 0 || refinementNotes.trim();

  // Bulk mode view
  if (bulkMode && bulkQuestions.length > 0) {
    const currentQuestion = bulkQuestions[bulkIndex];
    const suggestion = generateAnswerSuggestion(currentQuestion?.question || '', sectionKey, leanCanvas);
    const relatedChecks = getRelatedSanityChecks(currentQuestion?.question || '', section?.sanity_checks);

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setBulkMode(false)} />
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <div className="relative bg-background border border-border rounded-xl w-full max-w-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border-muted">
              <div className="flex items-center gap-3">
                {/* Mini depth indicator */}
                <div className="relative w-8 h-8 flex items-center justify-center">
                  {[1, 2, 3].map((level) => (
                    <div
                      key={level}
                      className={`absolute rounded-full border transition-all ${
                        level <= Math.min(3, depthLevel) ? 'border-accent' : 'border-border-muted'
                      }`}
                      style={{
                        width: `${level * 6 + 8}px`,
                        height: `${level * 6 + 8}px`,
                      }}
                    />
                  ))}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Quick Answer Mode</h2>
                  <p className="text-sm text-foreground-tertiary mt-0.5">
                    Question {bulkIndex + 1} of {bulkQuestions.length} • {totalAnswers} insights so far
                  </p>
                </div>
              </div>
              <button
                onClick={() => setBulkMode(false)}
                className="p-2 text-foreground-tertiary hover:text-foreground rounded-lg hover:bg-cream/10"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Progress through current batch */}
            <div className="px-6 pt-4">
              <div className="flex items-center justify-between text-xs text-foreground-muted mb-1">
                <span>This session</span>
                <span>{bulkIndex + 1}/{bulkQuestions.length}</span>
              </div>
              <div className="h-1.5 bg-cream/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-300"
                  style={{ width: `${((bulkIndex + 1) / bulkQuestions.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Question */}
            <div className="p-6">
              {currentQuestion && (
                <>
                  <div className="flex items-start gap-3 mb-4">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      currentQuestion.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                      currentQuestion.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-cream/10 text-foreground-tertiary'
                    }`}>
                      {currentQuestion.priority}
                    </span>
                    <span className="text-xs text-foreground-muted">{currentQuestion.category}</span>
                  </div>

                  <h3 className="text-lg text-foreground mb-2">{currentQuestion.question}</h3>
                  <p className="text-sm text-foreground-tertiary mb-4">{currentQuestion.context}</p>

                  {/* Related sanity checks */}
                  {relatedChecks.length > 0 && (
                    <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-xs text-yellow-400 font-medium mb-1">Addresses sanity check:</p>
                      {relatedChecks.map((check, i) => (
                        <p key={i} className="text-xs text-yellow-300">{check.message}</p>
                      ))}
                    </div>
                  )}

                  {/* Suggestion */}
                  {suggestion && (
                    <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-blue-400 font-medium mb-1">Suggested from your canvas:</p>
                      <p className="text-sm text-blue-300">{suggestion}</p>
                      <button
                        onClick={() => setQuestionAnswers(prev => ({ ...prev, [currentQuestion.question]: suggestion }))}
                        className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                      >
                        Use this suggestion
                      </button>
                    </div>
                  )}

                  <textarea
                    value={questionAnswers[currentQuestion.question] || ''}
                    onChange={(e) => updateAnswer(currentQuestion.question, e.target.value)}
                    placeholder="Type your answer..."
                    rows={4}
                    className="w-full px-4 py-3 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
                    autoFocus
                  />
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border-muted">
              <button
                onClick={() => {
                  if (bulkIndex > 0) setBulkIndex(bulkIndex - 1);
                }}
                disabled={bulkIndex === 0}
                className="px-4 py-2 text-foreground-tertiary hover:text-foreground disabled:opacity-30"
              >
                Previous
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (bulkIndex < bulkQuestions.length - 1) {
                      setBulkIndex(bulkIndex + 1);
                    } else {
                      setBulkMode(false);
                    }
                  }}
                  className="px-4 py-2 text-foreground-tertiary hover:text-foreground"
                >
                  Skip
                </button>
                <button
                  onClick={() => {
                    if (currentQuestion) {
                      saveAnswer(currentQuestion.question, questionAnswers[currentQuestion.question] || '', currentQuestion.id);
                    }
                  }}
                  disabled={isSavingAnswers || !questionAnswers[currentQuestion?.question || '']?.trim()}
                  className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50"
                >
                  {isSavingAnswers ? 'Saving...' : bulkIndex === bulkQuestions.length - 1 ? 'Save & Finish' : 'Save & Next'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Version history view
  if (showVersionHistory && versionHistory) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowVersionHistory(false)} />
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <div className="relative bg-background border border-border rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border-muted">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Version History</h2>
                <p className="text-sm text-foreground-tertiary mt-1">{versionHistory.versions.length} previous version(s)</p>
              </div>
              <button
                onClick={() => setShowVersionHistory(false)}
                className="p-2 text-foreground-tertiary hover:text-foreground rounded-lg hover:bg-cream/10"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {selectedVersion ? (
                <div>
                  <button
                    onClick={() => setSelectedVersion(null)}
                    className="mb-4 text-sm text-accent hover:underline"
                  >
                    ← Back to versions
                  </button>
                  <div className="mb-4">
                    <h3 className="text-lg text-foreground">Version {selectedVersion.version_number}</h3>
                    <p className="text-sm text-foreground-tertiary">
                      Generated {new Date(selectedVersion.generated_at).toLocaleString()}
                      {selectedVersion.trigger === 'refinement' && ' (refinement)'}
                    </p>
                    {selectedVersion.answers_used.length > 0 && (
                      <p className="text-sm text-foreground-muted mt-1">
                        {selectedVersion.answers_used.length} answer(s) incorporated
                      </p>
                    )}
                  </div>
                  <div className="p-4 bg-background/60 border border-border-muted rounded-lg max-h-96 overflow-y-auto">
                    <MarkdownRenderer content={selectedVersion.content_md} />
                  </div>
                  <button
                    onClick={() => restoreVersion(selectedVersion)}
                    className="mt-4 px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover"
                  >
                    Restore This Version
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {versionHistory.versions.map((version) => (
                    <button
                      key={version.id}
                      onClick={() => setSelectedVersion(version)}
                      className="w-full p-4 bg-background/60 border border-border-muted rounded-lg hover:border-border/30 text-left transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-foreground font-medium">Version {version.version_number}</h4>
                          <p className="text-sm text-foreground-tertiary">
                            {new Date(version.generated_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            version.trigger === 'initial' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300'
                          }`}>
                            {version.trigger}
                          </span>
                          {version.answers_used.length > 0 && (
                            <p className="text-xs text-foreground-muted mt-1">
                              {version.answers_used.length} answer(s)
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-muted mt-2 line-clamp-2">
                        {version.content_md.substring(0, 150)}...
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main modal view
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-background border border-border rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border-muted">
            <div>
              <h2 className="text-xl font-semibold text-foreground">{config.title}</h2>
              <p className="text-sm text-foreground-tertiary mt-1">
                Refine this section with additional context
              </p>
            </div>
            <div className="flex items-center gap-3">
              {versionHistory && versionHistory.versions.length > 0 && (
                <button
                  onClick={() => setShowVersionHistory(true)}
                  className="px-3 py-1.5 text-sm text-foreground-tertiary hover:text-foreground border border-border rounded-lg hover:border-border/40 transition-colors"
                >
                  History ({versionHistory.versions.length})
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-foreground-tertiary hover:text-foreground rounded-lg hover:bg-cream/10 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Refinement Depth Indicator */}
          <div className="px-6 py-4 border-b border-border-muted bg-background/40">
            <div className="flex items-center justify-between">
              {/* Depth visualization */}
              <div className="flex items-center gap-4">
                {/* Concentric rings showing depth */}
                <div className="relative w-12 h-12 flex items-center justify-center">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`absolute rounded-full border-2 transition-all duration-500 ${
                        level <= depthLevel
                          ? 'border-accent'
                          : 'border-border-muted'
                      }`}
                      style={{
                        width: `${level * 8 + 8}px`,
                        height: `${level * 8 + 8}px`,
                        opacity: level <= depthLevel ? 1 - (level - 1) * 0.15 : 0.3,
                      }}
                    />
                  ))}
                  <span className="text-sm font-bold text-accent z-10">{depthLevel}</span>
                </div>

                {/* Text stats */}
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {totalAnswers} insight{totalAnswers !== 1 ? 's' : ''} gathered
                  </div>
                  <div className="text-xs text-foreground-tertiary mt-0.5">
                    {answeredBaseQuestions > 0 && (
                      <span>{answeredBaseQuestions} base</span>
                    )}
                    {answeredFollowUps > 0 && (
                      <span>
                        {answeredBaseQuestions > 0 && ' + '}
                        {answeredFollowUps} follow-up{answeredFollowUps !== 1 ? 's' : ''}
                      </span>
                    )}
                    {totalAnswers === 0 && 'Answer questions to deepen your plan'}
                  </div>
                </div>
              </div>

              {/* Depth level badge */}
              <div className="text-right">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  depthLevel >= 4 ? 'bg-green-500/20 text-green-400' :
                  depthLevel >= 2 ? 'bg-accent-muted text-accent' :
                  'bg-cream/10 text-foreground-tertiary'
                }`}>
                  {depthLevel >= 5 ? '✦ Expert' :
                   depthLevel >= 4 ? '◆ Deep' :
                   depthLevel >= 3 ? '● Solid' :
                   depthLevel >= 2 ? '○ Growing' :
                   '○ Starting'}
                </div>
                {isGeneratingFollowUp && (
                  <div className="text-xs text-foreground-muted mt-1 flex items-center gap-1 justify-end">
                    <span className="animate-pulse">●</span> Generating follow-ups...
                  </div>
                )}
              </div>
            </div>

            {/* Quick answer mode link */}
            {filteredQuestions.length > 0 && (
              <button
                onClick={() => {
                  setBulkMode(true);
                  setBulkIndex(0);
                }}
                className="mt-3 text-sm text-accent hover:underline flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
                Quick Answer Mode ({filteredQuestions.length} questions available)
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Current Content Preview */}
            {section?.content_md && (
              <div>
                <h3 className="text-sm font-semibold text-foreground-tertiary mb-2">Current Content</h3>
                <div className="p-4 bg-background/60 border border-border-muted rounded-lg max-h-64 overflow-y-auto">
                  <MarkdownRenderer content={section.content_md} />
                </div>
              </div>
            )}

            {/* Charts Preview */}
            {section?.content_data && (
              <div className="space-y-4">
                {sectionKey === 'financial_projections' && section.content_data.financial && (
                  <FinancialProjectionsChart data={section.content_data.financial} height={200} />
                )}
                {sectionKey === 'market_analysis' && section.content_data.market && (
                  <MarketSizeChart data={section.content_data.market} height={180} />
                )}
              </div>
            )}

            {/* Saved Answers Section */}
            {(savedAnswers.length > 0 || isLoadingAnswers) && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-green-400">Your Saved Answers</h3>
                  <span className="text-xs text-foreground-muted">({savedAnswers.length} saved)</span>
                </div>

                {isLoadingAnswers ? (
                  <div className="grid gap-2">
                    {[1, 2].map((i) => (
                      <div key={i} className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg animate-pulse">
                        <div className="h-4 bg-cream/10 rounded w-3/4"></div>
                        <div className="h-3 bg-cream/10 rounded w-1/2 mt-2"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {savedAnswers.map((answer) => (
                      <div key={answer.id} className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="text-sm text-foreground-secondary font-medium">{answer.question_text}</div>
                            {editingAnswerId === answer.id ? (
                              <div className="mt-2">
                                <textarea
                                  value={editingAnswerText}
                                  onChange={(e) => setEditingAnswerText(e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground text-sm placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => updateSavedAnswer(answer.id, editingAnswerText)}
                                    disabled={isSavingAnswers || !editingAnswerText.trim()}
                                    className="px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 disabled:opacity-50 transition-colors"
                                  >
                                    {isSavingAnswers ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingAnswerId(null);
                                      setEditingAnswerText('');
                                    }}
                                    className="px-3 py-1 text-xs text-foreground-tertiary hover:text-foreground transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-green-300 mt-1">{answer.answer}</div>
                            )}
                          </div>
                          {editingAnswerId !== answer.id && (
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setEditingAnswerId(answer.id);
                                  setEditingAnswerText(answer.answer);
                                }}
                                className="p-1.5 text-foreground-muted hover:text-foreground hover:bg-cream/10 rounded transition-colors"
                                title="Edit answer"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Delete this answer?')) {
                                    deleteSavedAnswer(answer.id);
                                  }
                                }}
                                className="p-1.5 text-foreground-muted hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                title="Delete answer"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-foreground-muted mt-2">
                          Saved {new Date(answer.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sanity Check Issues */}
            {section?.sanity_checks && section.sanity_checks.filter((c) => !c.passed).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-yellow-400 mb-2">Issues to Address</h3>
                <div className="space-y-2">
                  {section.sanity_checks
                    .filter((c) => !c.passed)
                    .map((check, i) => (
                      <div
                        key={i}
                        className={`p-3 rounded-lg text-sm ${
                          check.severity === 'critical' || check.severity === 'error'
                            ? 'bg-red-500/20 border border-red-500/40 text-red-300'
                            : 'bg-yellow-500/20 border border-yellow-500/40 text-yellow-300'
                        }`}
                      >
                        <div className="font-medium">{check.check}</div>
                        <div className="text-xs mt-1 opacity-80">{check.message}</div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Category Tabs */}
            <div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <h3 className="text-sm font-semibold text-foreground-tertiary mr-2">Questions to Clarify</h3>
                {(['all', 'clarification', 'challenge', 'expansion', 'validation'] as QuestionCategory[]).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      selectedCategory === cat
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-cream/10 text-foreground-tertiary hover:bg-cream/20'
                    }`}
                  >
                    {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    {categoryCounts[cat] > 0 && (
                      <span className="ml-1 opacity-70">({categoryCounts[cat]})</span>
                    )}
                  </button>
                ))}
                {aiQuestions.length > 0 && !isLoadingQuestions && (
                  <span className="text-xs px-2 py-0.5 bg-accent-muted text-accent rounded-full ml-auto">
                    AI-generated
                  </span>
                )}
              </div>

              {isGeneratingFollowUp && (
                <p className="text-xs text-accent mb-2 animate-pulse">Generating follow-up questions...</p>
              )}

              {isLoadingQuestions ? (
                <div className="grid gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="p-3 bg-background/60 border border-border-muted rounded-lg animate-pulse">
                      <div className="h-4 bg-cream/10 rounded w-3/4"></div>
                      <div className="h-3 bg-cream/10 rounded w-1/2 mt-2"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {questionsError && (
                    <p className="text-xs text-yellow-400 mb-2">{questionsError}</p>
                  )}

                  {filteredQuestions.length > 0 ? (
                    <div className="grid gap-2">
                      {filteredQuestions.map((q) => {
                        const relatedChecks = getRelatedSanityChecks(q.question, section?.sanity_checks);
                        const suggestion = generateAnswerSuggestion(q.question, sectionKey, leanCanvas);

                        return (
                          <div
                            key={q.id}
                            className={`rounded-lg border transition-colors ${
                              selectedQuestions.has(q.question)
                                ? 'bg-accent-muted border-accent'
                                : 'bg-background/60 border-border-muted hover:border-border/30'
                            }`}
                          >
                            <button
                              onClick={() => toggleQuestion(q.question)}
                              className="w-full p-3 text-left"
                            >
                              <div className="flex items-start gap-2">
                                <span className={`mt-0.5 ${selectedQuestions.has(q.question) ? 'text-accent' : 'text-foreground-tertiary'}`}>
                                  {selectedQuestions.has(q.question) ? '✓' : '○'}
                                </span>
                                <div className="flex-1">
                                  <div className={`text-sm ${selectedQuestions.has(q.question) ? 'text-foreground' : 'text-foreground-secondary'}`}>
                                    {q.question}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      q.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                                      q.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                                      'bg-cream/10 text-foreground-tertiary'
                                    }`}>
                                      {q.priority}
                                    </span>
                                    <span className="text-xs text-foreground-muted">{q.category}</span>
                                    {relatedChecks.length > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-300 rounded">
                                        Fixes {relatedChecks.length} issue(s)
                                      </span>
                                    )}
                                    {suggestion && (
                                      <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                                        Has suggestion
                                      </span>
                                    )}
                                  </div>
                                  {q.context && (
                                    <p className="text-xs text-foreground-muted mt-1">{q.context}</p>
                                  )}
                                </div>
                              </div>
                            </button>

                            {/* Answer input when selected */}
                            {selectedQuestions.has(q.question) && (
                              <div className="px-3 pb-3 pt-1 space-y-2">
                                {suggestion && (
                                  <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded text-xs">
                                    <p className="text-blue-400 font-medium mb-1">Suggestion from canvas:</p>
                                    <p className="text-blue-300">{suggestion}</p>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateAnswer(q.question, suggestion);
                                      }}
                                      className="mt-1 text-blue-400 hover:text-blue-300"
                                    >
                                      Use suggestion
                                    </button>
                                  </div>
                                )}
                                <textarea
                                  value={questionAnswers[q.question] || ''}
                                  onChange={(e) => updateAnswer(q.question, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  placeholder="Type your answer here..."
                                  rows={2}
                                  className="w-full px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground text-sm placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
                                />
                                <div className="flex justify-end">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      saveAnswer(q.question, questionAnswers[q.question] || '', q.id);
                                    }}
                                    disabled={isSavingAnswers || !questionAnswers[q.question]?.trim()}
                                    className="px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {isSavingAnswers ? 'Saving...' : 'Save Answer'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-foreground-muted italic">
                      {savedAnswers.length > 0 ? 'All questions answered!' : 'No questions available for this category.'}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Refinement Notes */}
            <div>
              <h3 className="text-sm font-semibold text-foreground-tertiary mb-2">Additional Refinement Notes</h3>
              <textarea
                value={refinementNotes}
                onChange={(e) => setRefinementNotes(e.target.value)}
                placeholder="Add any specific details, corrections, or context you want included in this section..."
                className="w-full h-32 px-4 py-3 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
              />
              <p className="text-xs text-foreground-muted mt-1">
                Pro tip: Be specific about numbers, competitors, or strategies you want highlighted.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border-muted bg-background/40">
            <div className="text-sm text-foreground-tertiary">
              {savedAnswers.length > 0 && (
                <span className="text-green-400 mr-2">{savedAnswers.length} saved answer(s)</span>
              )}
              {selectedQuestions.size > 0 && (
                <span>
                  {selectedQuestions.size} question(s) selected
                  {Object.values(questionAnswers).filter(a => a?.trim()).length > 0 && (
                    <span className="text-accent ml-1">
                      ({Object.values(questionAnswers).filter(a => a?.trim()).length} with answers)
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-border text-foreground rounded-lg hover:border-border/40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating || !hasAnyContent}
                className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isRegenerating ? 'Regenerating...' : 'Regenerate Section'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
